import { Box, Typography, CircularProgress, Grid } from '@mui/material';
import { ConfirmAndPayLeftSide } from './ConfirmAndPayLeftSide';
import { ConfirmAndPayRightSide } from './ConfirmAndPayRightSide';
import {
  confirmAndPayTitleContainerSx,
  confirmAndPayTitleSx,
  flexContainerSx
} from '../../styles/createCampaign';
import { useCampaignData } from '../../hooks';
import { stripeLoaderSx } from 'src/common/CommomSxs';
import { useCitation } from '../../Providers/CitationProvider';
import { useEffect, useMemo, useState, useRef } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  confirmAndPaySchema,
  confirmAndPayDefaultValues,
  ConfirmAndPayFormData
} from './formSchema';
import { locationInfo } from 'src/common/Scraper';
import { LocationApiResponse } from '../../types';
import { parseOpeningHours } from '../../utils/parseOpeningHours';
import { Country } from 'country-state-city';
import { iso2ToIso3 } from 'src/content/localSeo/AddLocationDetailsComponents/funtions';
import { useLocation } from 'react-router';
import { useGetLocationInfoByLocationId } from '../../hooks/useGetLocationInfoByLocationId';

// Re-export type for convenience
export type { ConfirmAndPayFormData };

export const ConfirmAndPay = () => {
  const { state: locationState } = useLocation();
  const { locationId } = locationState as { locationId: string };
  const { shouldShowLoading, campaignId } = useCampaignData({
    incompleteLookupMessage:
      'Campaign lookup is still processing. Please try again later.'
  });

  const { state: citationState } = useCitation();

  const {
    data: locationDataByLocationId,
    isLoading: isLoadingLocationDetails,
    isFetching,
    refetch: refetchLocationDataByLocationId,
    error
  } = useGetLocationInfoByLocationId({ locationId: String(locationId) });


  const [isLoading, setIsLoading] = useState(false);
  const [locationDetails, setLocationDetails] = useState<any>(null);

  

  // Initialize react-hook-form with zod resolver
  const methods = useForm<ConfirmAndPayFormData>({
    resolver: zodResolver(confirmAndPaySchema),
    defaultValues: confirmAndPayDefaultValues,
    mode: 'onBlur'
  });

  const lastFetchedLocationId = useRef<string | null>(null);

  useEffect(() => {
    const fetchLocationDetails = async () => {
      if (citationState.selectedBusinessProfile) {
        const { accountId, name } = citationState.selectedBusinessProfile;
        const locationId = name.replace('locations/', '');

        // Prevent duplicate fetch for the same locationId
        if (lastFetchedLocationId.current === locationId) {
          return;
        }

        setIsLoading(true);
        lastFetchedLocationId.current = locationId;

        try {
          // fetch location details
          const locationDetails = (await locationInfo(
            locationId,
            accountId
          )) as LocationApiResponse;

          if (locationDetails.status === 'success') {
            updateFormWithData(locationDetails);
          }
        } catch (error) {
          console.error('Error fetching location details:', error);
          // Reset ref on error to allow retry
          lastFetchedLocationId.current = null;
        } finally {
          setIsLoading(false);
        }
      }
    };

    const fetchLocationByLocationId = async () => {
      refetchLocationDataByLocationId();
    };

    if (citationState.selectedBusinessProfile?.accountId && citationState.selectedBusinessProfile?.locationId) {
      fetchLocationDetails();
    } else if (locationId) {
      fetchLocationByLocationId();
    }
  }, [
    citationState.selectedBusinessProfile?.name,
    citationState.selectedBusinessProfile?.accountId,
    locationId
  ]);

  useEffect(() => {
    if (locationDataByLocationId) {
      updateFormWithData(locationDataByLocationId.data);
    }
  }, [locationDataByLocationId]);

  const updateFormWithData = (locationDetails: LocationApiResponse) => {
    setLocationDetails(locationDetails.locationInformation);
    methods.setValue(
      'businessLocationInfo.name',
      locationDetails.locationInformation.title
    );
    // methods.setValue(
    //   'businessLocationInfo.country',
    //   locationDetails.locationInformation.storefrontAddress.country
    // );
    methods.setValue(
      'businessLocationInfo.firstLine',
      locationDetails.locationInformation.storefrontAddress.addressLines[0]
    );
    methods.setValue(
      'businessLocationInfo.secondLine',
      locationDetails.locationInformation.storefrontAddress.addressLines[1]
    );
    methods.setValue(
      'businessLocationInfo.townCity',
      locationDetails.locationInformation.storefrontAddress.locality
    );
    methods.setValue(
      'businessLocationInfo.stateCountyRegion',
      locationDetails.locationInformation.storefrontAddress.regionCode
    );
    methods.setValue(
      'businessLocationInfo.postcode',
      locationDetails.locationInformation.storefrontAddress.postalCode
    );
    methods.setValue(
      'businessLocationInfo.businessPhoneNumber',
      locationDetails.locationInformation.phoneNumbers[0]
    );
    methods.setValue(
      'businessLocationInfo.businessWebsiteUrl',
      locationDetails.locationInformation.websiteUri
    );
    methods.setValue(
      'businessLocationContactInfo.contactPhoneNumber',
      locationDetails.locationInformation.phoneNumbers[0]
    );

    methods.setValue(
      'aboutTheBusiness.businessDescription',
      locationDetails.locationInformation.description
    );
    // set opening hours
    const openingHours = parseOpeningHours(
      locationDetails.locationInformation.regularHours
    );
    methods.setValue('aboutTheBusiness.openingHours', openingHours);

    const allowedCountries = ['US', 'GB', 'CA', 'AU', 'NZ'];
    const filtered = Country.getAllCountries().filter((c) =>
      allowedCountries.includes(c.isoCode)
    );

    const matchedCountry = filtered?.find(
      (c) =>
        c.name === locationDetails.locationInformation.storefrontAddress.country
    );
    const iso3 = matchedCountry?.isoCode
      ? iso2ToIso3[matchedCountry?.isoCode]
      : matchedCountry?.isoCode;
    methods.setValue('businessLocationInfo.country', iso3);
  };

  const campaignName = useMemo<string>(() => {
    if (!locationDetails?.title) {
      return '';
    }
    return locationDetails?.title;
  }, [locationDetails]);

  if (shouldShowLoading || isLoading || isLoadingLocationDetails) {
    return (
      <Grid item xs={12}>
        <Box sx={stripeLoaderSx?.stripeLoaderBx}>
          <CircularProgress />
        </Box>
      </Grid>
    );
  }

  if (!campaignId) {
    return null;
  }

  return (
    <>
      <Box sx={confirmAndPayTitleContainerSx}>
        <Typography sx={confirmAndPayTitleSx}>
          Your Citation Campaign for &quot;{campaignName}&quot;
        </Typography>
      </Box>

      <FormProvider {...methods}>
        <Box
          component="form"
          onSubmit={(e) => e.preventDefault()}
          id="confirm-and-pay-form"
          noValidate
        >
          <Box sx={flexContainerSx}>
            <ConfirmAndPayLeftSide locationDetails={locationDetails} />
            <ConfirmAndPayRightSide />
          </Box>
        </Box>
      </FormProvider>
    </>
  );
};
